import React, { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Switch } from '../../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../../../components/ui/command';
import { toast } from 'sonner';
import {
  Calculator, Shield, Percent, Landmark, History, Check, ChevronsUpDown,
  Save, PlayCircle, Download, Layers, AlertTriangle, Users2,
} from 'lucide-react';

const AUDIT_LABELS = {
  rank_changed: { label: 'Rank changed', color: 'bg-purple-100 text-purple-700' },
  upline_changed: { label: 'Upline changed', color: 'bg-blue-100 text-blue-700' },
  employment_changed: { label: 'Employment changed', color: 'bg-amber-100 text-amber-700' },
  comm_engine_settings_updated: { label: 'Settings updated', color: 'bg-emerald-100 text-emerald-700' },
  wht_override_set: { label: 'WHT override', color: 'bg-rose-100 text-rose-700' },
};

const money = (v) => `RM ${Number(v || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CoBrokeEditor = ({ bookingId, agents, participants, onSaved }) => {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    setRows((participants || []).map((p) => ({ agent_id: p.agent_id, allocation_percent: p.allocation_percent })));
  }, [participants]);

  const total = rows.reduce((s, r) => s + (parseFloat(r.allocation_percent) || 0), 0);
  const nameFor = (id) => {
    const u = agents.find((x) => x.id === id);
    return u ? `${u.agent_code || u.user_code || ''} · ${u.full_name}` : (id ? id.slice(0, 8) : 'Select agent');
  };

  const addRow = () => { if (rows.length < 4) setRows([...rows, { agent_id: '', allocation_percent: 0 }]); };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const setRow = (i, patch) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    if (rows.some((r) => !r.agent_id)) { toast.error('Pick an agent for every row'); return; }
    if (Math.abs(total - 100) > 0.01) { toast.error(`Allocations must total 100% (now ${total}%)`); return; }
    setSaving(true);
    try {
      await api.put(`/comm-engine/bookings/${bookingId}/co-broke`, {
        participants: rows.map((r) => ({ agent_id: r.agent_id, allocation_percent: parseFloat(r.allocation_percent) })),
      });
      toast.success('Co-broke participants saved — re-running dry-run');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save co-broke');
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="co-broke-editor">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Users2 className="w-4 h-4 text-indigo-600" /> Co-Broke Participants (max 4 · manual %)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-500">Each participant's allocation follows their OWN leader hierarchy independently. Allocations must total exactly 100%.</p>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <Popover open={openIdx === i} onOpenChange={(o) => setOpenIdx(o ? i : null)}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="flex-1 justify-between font-normal" data-testid={`co-broke-agent-${i}`}>
                  <span className="truncate">{r.agent_id ? nameFor(r.agent_id) : <span className="text-gray-400">Select agent</span>}</span>
                  <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command filter={(value, search) => {
                  const opt = agents.find((u) => u.id === value); if (!opt) return 0;
                  const norm = (s) => String(s || '').toLowerCase().replace(/[\s-]/g, '');
                  const hay = norm(`${opt.agent_code || ''} ${opt.user_code || ''} ${opt.full_name || ''} ${opt.email || ''}`);
                  return hay.includes(norm(search)) ? 1 : 0;
                }}>
                  <CommandInput placeholder="Search agent…" />
                  <CommandList>
                    <CommandEmpty>No agent found.</CommandEmpty>
                    <CommandGroup>
                      {agents.map((u) => (
                        <CommandItem key={u.id} value={u.id} onSelect={(val) => { setRow(i, { agent_id: val }); setOpenIdx(null); }} data-testid={`co-broke-option-${i}-${u.id}`}>
                          <Check className={`mr-2 h-4 w-4 ${r.agent_id === u.id ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="text-sm">{u.agent_code || u.user_code} · {u.full_name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Input type="number" className="w-24" value={r.allocation_percent}
              onChange={(e) => setRow(i, { allocation_percent: e.target.value })} data-testid={`co-broke-pct-${i}`} placeholder="%" />
            <Button variant="ghost" size="icon" className="text-rose-500" onClick={() => removeRow(i)} data-testid={`co-broke-remove-${i}`}>✕</Button>
          </div>
        ))}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={addRow} disabled={rows.length >= 4} data-testid="co-broke-add">+ Add agent</Button>
            <Badge className={Math.abs(total - 100) < 0.01 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'} data-testid="co-broke-total">Total {total}%</Badge>
          </div>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} disabled={saving} data-testid="co-broke-save">
            {saving ? 'Saving…' : 'Save & Re-run'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const CommEngineOps = () => {
  const [settings, setSettings] = useState(null);
  const [agents, setAgents] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);

  const [whtRatePct, setWhtRatePct] = useState('2');
  const [whtThreshold, setWhtThreshold] = useState('100000');
  const [fallbackId, setFallbackId] = useState('');
  const [v2Enabled, setV2Enabled] = useState(true);

  // Dry-run state
  const [bookingId, setBookingId] = useState('');
  const [priceOverride, setPriceOverride] = useState('');
  const [fastOption, setFastOption] = useState('wait_0');
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sres, ares, ures] = await Promise.all([
        api.get('/comm-engine/settings'),
        api.get('/comm-engine/audit?limit=25'),
        api.get('/users', { params: { role: 'agent,neg,rm,avp,svp,evp', limit: 3000 } }),
      ]);
      const s = sres.data.settings;
      setSettings(s);
      setWhtRatePct(String(((s.wht_rate ?? 0.02) * 100)));
      setWhtThreshold(String(s.wht_threshold ?? 100000));
      setFallbackId(s.fallback_evp_agent_id || '');
      setV2Enabled(s.v2_engine_enabled !== false);
      setAudit(ares.data.items || []);
      setAgents(ures.data || []);
    } catch (e) {
      toast.error('Failed to load commission engine data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const ratePct = parseFloat(whtRatePct);
    const threshold = parseFloat(whtThreshold);
    if (isNaN(ratePct) || ratePct < 0 || ratePct > 100) { toast.error('WHT rate must be 0–100%'); return; }
    if (isNaN(threshold) || threshold < 0) { toast.error('Threshold must be positive'); return; }
    setSaving(true);
    try {
      const res = await api.put('/comm-engine/settings', {
        wht_rate: ratePct / 100, wht_threshold: threshold, fallback_evp_agent_id: fallbackId || '',
        v2_engine_enabled: v2Enabled,
      });
      setSettings(res.data.settings);
      toast.success('Commission engine settings saved');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  const runPreview = async () => {
    if (!bookingId.trim()) { toast.error('Enter a booking ID'); return; }
    setPreviewing(true);
    setPreview(null);
    try {
      const body = { booking_id: bookingId.trim(), fast_commission_option: fastOption };
      if (priceOverride) body.price = parseFloat(priceOverride);
      const res = await api.post('/comm-engine/preview', body);
      setPreview(res.data);
      toast.success('Dry-run computed (nothing persisted)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setPreviewing(false); }
  };

  const downloadReplayDiff = async () => {
    try {
      const res = await api.get('/comm-engine/replay-diff?limit=1000&format=csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'comm_engine_replay_diff.csv';
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success('Replay-diff CSV downloaded');
    } catch (e) {
      toast.error('Failed to download replay-diff');
    }
  };

  const selectedAgent = agents.find((a) => a.id === fallbackId);
  const nameFor = (id) => {
    const u = agents.find((x) => x.id === id);
    return u ? `${u.agent_code || u.user_code || ''} · ${u.full_name}` : (id ? id.slice(0, 8) : '—');
  };

  return (
    <div className="p-6 space-y-6" data-testid="comm-engine-ops-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-indigo-600 flex items-center justify-center text-white">
          <Calculator className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Commission Engine</h1>
          <p className="text-sm text-gray-500">Rank Engine V2 — settings, snapshots &amp; dry-run preview</p>
        </div>
        <Badge className="ml-auto bg-indigo-50 text-indigo-700 border border-indigo-200">V2</Badge>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full" />
        </div>
      ) : (
        <Tabs defaultValue="settings">
          <TabsList data-testid="comm-engine-tabs">
            <TabsTrigger value="settings" data-testid="tab-settings">Policy &amp; Settings</TabsTrigger>
            <TabsTrigger value="dryrun" data-testid="tab-dryrun">Dry-Run &amp; Snapshots</TabsTrigger>
          </TabsList>

          {/* ---------------- Settings ---------------- */}
          <TabsContent value="settings" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card className={v2Enabled ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40'} data-testid="v2-cutover-card">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${v2Enabled ? 'bg-emerald-600' : 'bg-amber-500'} text-white`}>
                      <Calculator className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">V2 Engine Cutover</p>
                      <p className="text-xs text-gray-500">
                        {v2Enabled
                          ? 'New commission claims are computed with the V2 engine (SST → platform → WHT → final payout).'
                          : 'New claims use the legacy calculation. Turn on to apply the V2 engine.'}
                      </p>
                    </div>
                    <Badge className={v2Enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {v2Enabled ? 'V2 ACTIVE' : 'LEGACY'}
                    </Badge>
                    <Switch checked={v2Enabled} onCheckedChange={setV2Enabled} data-testid="v2-cutover-toggle" />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Percent className="w-4 h-4 text-indigo-600" /> Withholding Tax Policy
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-500 leading-relaxed">
                      An agent becomes subject to withholding tax when their{' '}
                      <strong>previous calendar year</strong> total commission reaches the threshold.
                      Once eligible, the rate is deducted from all their net claims for the whole current year.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700">Withholding Tax Rate (%)</label>
                        <Input type="number" step="0.1" value={whtRatePct}
                          onChange={(e) => setWhtRatePct(e.target.value)} data-testid="wht-rate-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Previous-Year Threshold (RM)</label>
                        <Input type="number" step="1000" value={whtThreshold}
                          onChange={(e) => setWhtThreshold(e.target.value)} data-testid="wht-threshold-input" />
                      </div>
                    </div>
                    <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 text-xs text-indigo-700">
                      Current policy: agents with over <strong>RM {Number(whtThreshold || 0).toLocaleString()}</strong> in
                      the previous year are charged <strong>{whtRatePct || 0}%</strong> withholding tax.
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Landmark className="w-4 h-4 text-indigo-600" /> Fallback EVP Recipient
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-500 leading-relaxed">
                      When no eligible upline exists for a required leader layer, the unallocated
                      commission is assigned to this configurable Sam Realty EVP account.
                    </p>
                    <Popover open={agentPickerOpen} onOpenChange={setAgentPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox"
                          className="w-full justify-between font-normal" data-testid="fallback-agent-trigger">
                          {selectedAgent ? (
                            <span className="truncate">{selectedAgent.agent_code || selectedAgent.user_code || '—'} · {selectedAgent.full_name}</span>
                          ) : (<span className="text-gray-400">Select fallback EVP account (optional)</span>)}
                          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command filter={(value, search) => {
                          const opt = agents.find((u) => u.id === value);
                          if (!opt) return 0;
                          const hay = `${opt.agent_code || ''} ${opt.user_code || ''} ${opt.full_name || ''} ${opt.email || ''}`.toLowerCase();
                          return hay.includes(search.toLowerCase()) ? 1 : 0;
                        }}>
                          <CommandInput placeholder="Search agent by name / code…" data-testid="fallback-agent-search" />
                          <CommandList>
                            <CommandEmpty>No agent found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem value="" onSelect={() => { setFallbackId(''); setAgentPickerOpen(false); }} data-testid="fallback-agent-none">
                                <Check className={`mr-2 h-4 w-4 ${!fallbackId ? 'opacity-100' : 'opacity-0'}`} />
                                <span className="text-gray-500 italic">No fallback set</span>
                              </CommandItem>
                              {agents.map((u) => (
                                <CommandItem key={u.id} value={u.id}
                                  onSelect={(val) => { setFallbackId(val); setAgentPickerOpen(false); }}
                                  data-testid={`fallback-agent-option-${u.id}`}>
                                  <Check className={`mr-2 h-4 w-4 ${fallbackId === u.id ? 'opacity-100' : 'opacity-0'}`} />
                                  <div className="flex flex-col">
                                    <span className="font-mono text-xs text-gray-600">{u.agent_code || u.user_code || '—'} · {(u.agent_rank || u.role || '').toUpperCase()}</span>
                                    <span className="text-sm">{u.full_name}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </CardContent>
                </Card>

                <div className="flex justify-end">
                  <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSave} disabled={saving} data-testid="save-comm-engine-settings">
                    <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save Settings'}
                  </Button>
                </div>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base"><History className="w-4 h-4 text-indigo-600" /> Recent Engine Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {audit.length === 0 ? (
                      <div className="text-center text-gray-400 py-8 text-sm"><Shield className="w-10 h-10 mx-auto mb-2 text-gray-200" />No engine activity yet</div>
                    ) : (
                      <div className="space-y-3" data-testid="comm-engine-audit-list">
                        {audit.map((row) => {
                          const meta = AUDIT_LABELS[row.action] || { label: row.action, color: 'bg-gray-100 text-gray-600' };
                          return (
                            <div key={row.id} className="flex items-start gap-3 border-b last:border-b-0 pb-3 last:pb-0">
                              <Badge className={`${meta.color} text-[10px] shrink-0`}>{meta.label}</Badge>
                              <div className="min-w-0 flex-1">
                                <p className="text-xs text-gray-500 truncate">{row.reason || row.notes || 'No reason recorded'}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">{row.changed_at ? new Date(row.changed_at).toLocaleString() : ''}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* ---------------- Dry-run ---------------- */}
          <TabsContent value="dryrun" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <PlayCircle className="w-4 h-4 text-indigo-600" /> Engine V2 Dry-Run Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-500">
                  Replays the V2 engine against a booking's frozen creation snapshot. Nothing is persisted —
                  this is a safe sign-off tool before cutover.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-gray-700">Booking ID</label>
                    <Input value={bookingId} onChange={(e) => setBookingId(e.target.value)} placeholder="Paste a booking id" data-testid="preview-booking-id" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Price override (optional)</label>
                    <Input type="number" value={priceOverride} onChange={(e) => setPriceOverride(e.target.value)} placeholder="e.g. 500000" data-testid="preview-price" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700">Fast Commission</label>
                    <Select value={fastOption} onValueChange={setFastOption}>
                      <SelectTrigger data-testid="preview-fast-option"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wait_0">Wait (0%)</SelectItem>
                        <SelectItem value="upfront_50">Upfront 50%</SelectItem>
                        <SelectItem value="upfront_100">Upfront 100%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={runPreview} disabled={previewing} data-testid="run-preview-btn">
                    <PlayCircle className="w-4 h-4 mr-2" />{previewing ? 'Computing…' : 'Run Dry-Run'}
                  </Button>
                  <Button variant="outline" onClick={downloadReplayDiff} data-testid="download-replay-diff-btn">
                    <Download className="w-4 h-4 mr-2" /> Replay-Diff CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            {preview && (
              <div className="space-y-6" data-testid="preview-result">
                {/* Snapshot summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base"><Layers className="w-4 h-4 text-indigo-600" /> Frozen Snapshot (booking-creation)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-4 text-sm mb-4">
                      <div><span className="text-gray-500">Cutoff:</span> <span className="font-medium">{preview.snapshot?.cutoff_at ? new Date(preview.snapshot.cutoff_at).toLocaleString() : '—'}</span></div>
                      <div><span className="text-gray-500">Base price:</span> <span className="font-medium">{money(preview.base_price)}</span> <span className="text-xs text-gray-400">({preview.price_source})</span></div>
                      <div><span className="text-gray-500">Allocation:</span> {preview.allocation_valid ? <Badge className="bg-emerald-100 text-emerald-700">100% valid</Badge> : <Badge className="bg-rose-100 text-rose-700">≠ 100%</Badge>}</div>
                    </div>
                    <div className="space-y-2">
                      {(preview.snapshot?.participants || []).map((p, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm border rounded-lg p-2">
                          <Badge className="bg-purple-100 text-purple-700">{(p.rank || '').toUpperCase()}</Badge>
                          <span className="font-medium">{p.agent_code} · {p.full_name}</span>
                          <span className="text-gray-500">alloc {p.allocation_percent}%</span>
                          <span className="text-gray-400 text-xs">{p.employment_status}</span>
                          {p.wht_eligible && <Badge className="bg-amber-100 text-amber-700 ml-auto">WHT {(p.wht_rate * 100).toFixed(1)}%</Badge>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Branch breakdowns */}
                <CoBrokeEditor
                  bookingId={preview.snapshot?.booking_id || bookingId}
                  agents={agents}
                  participants={preview.snapshot?.participants || []}
                  onSaved={runPreview}
                />
                {preview.branches.map((b, bi) => (
                  <Card key={bi}>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center justify-between">
                        <span>{b.agent_code} — branch {b.allocation_percent}% · {money(b.branch_price)}</span>
                        <span className="text-sm text-emerald-600">Payout {money(b.branch_total_final_payout)}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                            <tr>
                              <th className="text-left p-2">Line</th><th className="text-left p-2">Recipient</th>
                              <th className="text-right p-2">Rate</th><th className="text-right p-2">Gross</th>
                              <th className="text-right p-2">SST</th><th className="text-right p-2">Platform</th>
                              <th className="text-right p-2">Nett</th><th className="text-right p-2">WHT</th>
                              <th className="text-right p-2">Payout</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-t">
                              <td className="p-2 font-medium">Seller</td>
                              <td className="p-2">{b.seller_line.recipient_code}</td>
                              <td className="p-2 text-right">{(b.seller_line.rate * 100).toFixed(2)}%</td>
                              <td className="p-2 text-right">{money(b.seller_line.gross)}</td>
                              <td className="p-2 text-right text-rose-500">-{money(b.seller_line.sst)}</td>
                              <td className="p-2 text-right text-rose-500">-{money(b.seller_line.platform_fee)}</td>
                              <td className="p-2 text-right">{money(b.seller_line.nett)}</td>
                              <td className="p-2 text-right text-amber-600">{b.seller_line.wht > 0 ? `-${money(b.seller_line.wht)}` : '—'}</td>
                              <td className="p-2 text-right font-semibold text-emerald-600">{money(b.seller_line.final_payout)}</td>
                            </tr>
                            {b.leader_rows.map((r, ri) => (
                              <tr key={ri} className="border-t">
                                <td className="p-2">{r.layer.toUpperCase()}</td>
                                <td className="p-2">{r.fallback ? <span className="text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Fallback EVP</span> : (r.recipient_code || nameFor(r.recipient_id))}</td>
                                <td className="p-2 text-right">{(r.rate * 100).toFixed(2)}%</td>
                                <td className="p-2 text-right">{money(r.gross)}</td>
                                <td className="p-2 text-right text-rose-500">-{money(r.sst)}</td>
                                <td className="p-2 text-right text-rose-500">-{money(r.platform_fee)}</td>
                                <td className="p-2 text-right">{money(r.nett)}</td>
                                <td className="p-2 text-right">—</td>
                                <td className="p-2 text-right font-semibold text-emerald-600">{money(r.final_payout)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {b.seller_line.fast_commission_option !== 'wait_0' && (
                        <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-blue-700">
                          Fast Commission ({b.seller_line.fast_commission_option.replace('upfront_', '')}%): advance {money(b.seller_line.fast_commission_advance)} · fee {money(b.seller_line.fast_commission_fee)}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {/* Totals */}
                <Card className="border-indigo-200">
                  <CardContent className="p-4 flex flex-wrap gap-8">
                    <div><p className="text-xs text-gray-500">Total Nett</p><p className="text-lg font-bold">{money(preview.totals.nett)}</p></div>
                    <div><p className="text-xs text-gray-500">Total WHT</p><p className="text-lg font-bold text-amber-600">-{money(preview.totals.wht)}</p></div>
                    <div><p className="text-xs text-gray-500">Total Final Payout</p><p className="text-lg font-bold text-emerald-600">{money(preview.totals.final_payout)}</p></div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default CommEngineOps;
